import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import Cookies from 'universal-cookie';
import { useTranslation } from 'react-i18next';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { API_GET, DBRequest, useConfig } from '../../../api/api';
import ExoDialog from '../../../components/exo/ExoDialog';
import ExoAvatar from '../../../components/exo/ExoAvatar';
import { tokens } from '../../../global/theme/tokens';

const TimeTrackingExportDialog = ({
  open,
  onClose,
  project,
  dateStart,
  dateEnd,
  teamUsers = [],
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedWorkPackages, setSelectedWorkPackages] = useState([]);
  const [includeWithoutWorkPackage, setIncludeWithoutWorkPackage] = useState(true);
  const [phases, setPhases] = useState(null);
  const [isLoadingPhases, setIsLoadingPhases] = useState(false);
  const [localDateStart, setLocalDateStart] = useState(null);
  const [localDateEnd, setLocalDateEnd] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const dateFormat = config?.general?.dateOutputFormat || 'YYYY-MM-DD';

  // Dialog öffnen: Datum setzen und alle Benutzer auswählen
  useEffect(() => {
    if (open) {
      setLocalDateStart(dateStart ? moment(dateStart) : null);
      setLocalDateEnd(dateEnd ? moment(dateEnd) : null);
      // Alle Teammitglieder standardmäßig auswählen
      setSelectedUsers(teamUsers.map((u) => u.id));
    }
  }, [open, dateStart, dateEnd, teamUsers]);

  // Phasen laden und alle Arbeitspakete standardmäßig auswählen
  useEffect(() => {
    if (!config || !project || !open) return;

    setIsLoadingPhases(true);
    setIncludeWithoutWorkPackage(true);
    DBRequest({
      config,
      path: `projects/${project.id}/phases`,
      method: API_GET,
      onResponse: (res) => {
        const phasesData = res.data || [];
        setPhases(phasesData);
        // Alle Arbeitspakete standardmäßig auswählen
        const allWpIds = phasesData.flatMap((phase) =>
          (phase.workPackages || []).map((wp) => wp.id)
        );
        setSelectedWorkPackages(allWpIds);
        setIsLoadingPhases(false);
      },
    });
  }, [config, project, open]);

  const getAllWorkPackages = () => {
    if (!phases) return [];
    return phases.flatMap((phase) => phase.workPackages || []);
  };

  const toggleUser = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleWorkPackage = (wpId) => {
    setSelectedWorkPackages((prev) =>
      prev.includes(wpId) ? prev.filter((id) => id !== wpId) : [...prev, wpId]
    );
  };

  const handleSelectAllUsers = () => {
    if (selectedUsers.length === teamUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(teamUsers.map((u) => u.id));
    }
  };

  const handleSelectAllWorkPackages = () => {
    const allWpIds = getAllWorkPackages().map((wp) => wp.id);
    const allSelected =
      selectedWorkPackages.length === allWpIds.length && includeWithoutWorkPackage;
    if (allSelected) {
      setSelectedWorkPackages([]);
      setIncludeWithoutWorkPackage(false);
    } else {
      setSelectedWorkPackages(allWpIds);
      setIncludeWithoutWorkPackage(true);
    }
  };

  const buildExportUrl = () => {
    const params = new URLSearchParams();

    // Filter format: key[operator]=value
    params.append('projectId[eq]', project.id);

    if (localDateStart) {
      params.append('timeStart[gte]', localDateStart.format('YYYY-MM-DD'));
    }
    if (localDateEnd) {
      params.append('timeStart[lte]', localDateEnd.format('YYYY-MM-DD') + ' 23:59:59');
    }

    // Benutzer-IDs als Array senden
    selectedUsers.forEach((userId) => {
      params.append('userIds[]', userId);
    });

    // Arbeitspaket-IDs als Array senden
    selectedWorkPackages.forEach((wpId) => {
      params.append('workPackageIds[]', wpId);
    });

    // Flag für Zeiten ohne Arbeitspaket
    params.append('includeWithoutWorkPackage', includeWithoutWorkPackage ? '1' : '0');

    return `${config.API_URL}export-time-trackings?${params.toString()}`;
  };

  const handleExport = async () => {
    setIsExporting(true);
    const cookies = new Cookies();
    const url = buildExportUrl();

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${cookies.get('accessToken')}`,
        },
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `zeiterfassung_${project.id}_${moment().format('YYYY-MM-DD')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      onClose();
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setIsExporting(false);
    }
  };

  // Styling für selektierbare Elemente
  const getSelectableStyle = (isSelected) => ({
    cursor: 'pointer',
    borderRadius: '12px',
    border: `1px solid ${isSelected ? colors.info + '80' : colors.grey[600]}`,
    backgroundColor: isSelected ? colors.info + '30' : 'transparent',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: colors.info + '80',
      backgroundColor: isSelected ? colors.info + '40' : colors.info + '15',
    },
  });

  return (
    <ExoDialog open={open} onClose={onClose} limitWidth maxWidth="650px">
      <Box className="flex flex-col gap-5 relative h-full overflow-y-auto">
        <Typography variant="h3" textAlign="center" className="py-2">
          {t('Zeiterfassung exportieren')}
        </Typography>

        {/* Datumsbereich */}
        <Box
          className="p-4 rounded-xl"
          sx={{ backgroundColor: colors.bgInput }}
        >
          <Box className="flex justify-between items-center mb-3">
            <Typography variant="h5">{t('Zeitraum')}</Typography>
            <Button
              size="small"
              onClick={() => {
                setLocalDateStart(null);
                setLocalDateEnd(null);
              }}
              disabled={!localDateStart && !localDateEnd}
            >
              {t('Gesamter Zeitraum')}
            </Button>
          </Box>
          <Box className="flex flex-row gap-3 w-full items-center">
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                className="w-full"
                label={t('Von')}
                value={localDateStart}
                onChange={setLocalDateStart}
                format={dateFormat}
              />
              <Typography sx={{ color: colors.grey[500] }}>–</Typography>
              <DatePicker
                className="w-full"
                label={t('Bis')}
                value={localDateEnd}
                onChange={setLocalDateEnd}
                format={dateFormat}
              />
            </LocalizationProvider>
          </Box>
        </Box>

        {/* Teammitglieder */}
        <Box>
          <Box className="flex justify-between items-center mb-3">
            <Typography variant="h5">{t('Teammitglieder')}</Typography>
            {teamUsers.length > 0 && (
              <Button size="small" onClick={handleSelectAllUsers}>
                {selectedUsers.length === teamUsers.length
                  ? t('Alle abwählen')
                  : t('Alle auswählen')}
              </Button>
            )}
          </Box>
          {teamUsers.length > 0 ? (
            <Box className="flex flex-wrap gap-2">
              {teamUsers.map((user) => {
                const isSelected = selectedUsers.includes(user.id);
                return (
                  <Box
                    key={user.id}
                    onClick={() => toggleUser(user.id)}
                    className="flex items-center gap-2 px-3 py-2"
                    sx={getSelectableStyle(isSelected)}
                  >
                    <ExoAvatar
                      name={`${user.firstName} ${user.lastName}`}
                      icon={user.icon}
                      size="32px"
                    />
                    <Typography variant="body2">
                      {user.firstName} {user.lastName}
                    </Typography>
                    {isSelected && (
                      <CheckCircleIcon
                        sx={{ fontSize: 18, color: colors.info }}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: colors.grey[500] }}>
              {t('Keine Teammitglieder mit erfassten Zeiten')}
            </Typography>
          )}
        </Box>

        {/* Arbeitspakete */}
        <Box>
          <Box className="flex justify-between items-center mb-3">
            <Typography variant="h5">{t('Arbeitspakete')}</Typography>
            {getAllWorkPackages().length > 0 && (
              <Button size="small" onClick={handleSelectAllWorkPackages}>
                {selectedWorkPackages.length === getAllWorkPackages().length &&
                includeWithoutWorkPackage
                  ? t('Alle abwählen')
                  : t('Alle auswählen')}
              </Button>
            )}
          </Box>

          {/* Option für Zeiten ohne Arbeitspaket */}
          <Box className="flex flex-wrap gap-2 mb-4">
            <Box
              onClick={() => setIncludeWithoutWorkPackage(!includeWithoutWorkPackage)}
              className="flex items-center gap-2 px-3 py-2"
              sx={getSelectableStyle(includeWithoutWorkPackage)}
            >
              <Box
                className="w-6 h-6 rounded flex items-center justify-center"
                sx={{ backgroundColor: colors.grey[500] }}
              >
                <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600 }}>
                  ?
                </Typography>
              </Box>
              <Typography variant="body2">
                {t('Projektzeiten (ohne Arbeitspaket)')}
              </Typography>
              {includeWithoutWorkPackage && (
                <CheckCircleIcon sx={{ fontSize: 18, color: colors.info }} />
              )}
            </Box>
          </Box>

          {isLoadingPhases ? (
            <Box className="flex justify-center py-6">
              <CircularProgress size={28} />
            </Box>
          ) : phases && phases.length > 0 ? (
            <Box className="flex flex-col gap-4">
              {phases.map((phase) => (
                <Box key={phase.id}>
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    sx={{ mb: 2 }}
                  >
                    {phase.name}
                  </Typography>
                  {phase.workPackages && phase.workPackages.length > 0 ? (
                    <Box className="flex flex-wrap gap-2">
                      {phase.workPackages.map((wp) => {
                        const isSelected = selectedWorkPackages.includes(wp.id);
                        return (
                          <Box
                            key={wp.id}
                            onClick={() => toggleWorkPackage(wp.id)}
                            className="flex items-center gap-2 px-3 py-2"
                            sx={getSelectableStyle(isSelected)}
                          >
                            {wp.icon?.temporaryUrl ? (
                              <img
                                src={wp.icon.temporaryUrl}
                                alt=""
                                className="w-6 h-6 rounded object-cover"
                              />
                            ) : (
                              <Box
                                className="w-6 h-6 rounded"
                                sx={{ backgroundColor: colors.grey[600] }}
                              />
                            )}
                            <Typography variant="body2">{wp.title}</Typography>
                            {isSelected && (
                              <CheckCircleIcon
                                sx={{ fontSize: 18, color: colors.info }}
                              />
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{ color: colors.grey[500] }}
                    >
                      {t('Keine Arbeitspakete')}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: colors.grey[500] }}>
              {t('Keine Arbeitspakete vorhanden')}
            </Typography>
          )}
        </Box>

        {/* Aktionsbuttons */}
        <Box className="flex flex-row w-full items-center justify-between pt-3">
          <Button
            variant="outlined"
            color="error"
            onClick={onClose}
            disabled={isExporting}
          >
            {t('Abbrechen')}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleExport}
            disabled={isExporting}
            startIcon={
              isExporting ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <FileDownloadIcon />
              )
            }
          >
            {t('CSV exportieren')}
          </Button>
        </Box>
      </Box>
    </ExoDialog>
  );
};

export default TimeTrackingExportDialog;
